import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { User, Star, TrendingUp, MessageSquare, Check, X, Pencil, Trash2, Save, Loader2 } from 'lucide-react';
import { groupFieldsIntoSections } from '@/components/zoho/fieldGrouping';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';

var SECTION_ICONS = {
  identity: User,
  performance: Star,
  compensation: TrendingUp,
  remarks: MessageSquare,
};

function statusBadge(s) {
  if (s === 'accepted') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s === 'rejected') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function statusLabel(s) {
  if (s === 'accepted') return 'Accepted';
  if (s === 'rejected') return 'Rejected';
  return 'Pending Review';
}

export default function SubmDetail({ entity, open, onClose, onApprove, onReject, onRefresh }) {
  if (!entity) return null;

  var latestSub = entity.submissions && entity.submissions[0];
  var payload = latestSub ? latestSub.raw_payload || {} : {};
  var approval = latestSub ? latestSub.approval_status || 'pending' : 'pending';
  var subId = latestSub ? latestSub.id : null;
  var isPending = approval === 'pending';
  var isAccepted = approval === 'accepted';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="subm-detail-sheet">
        <DetailHeader entity={entity} approval={approval} />
        <DetailActions
          isPending={isPending}
          isAccepted={isAccepted}
          subId={subId}
          onApprove={onApprove}
          onReject={onReject}
          onClose={onClose}
          onRefresh={onRefresh}
        />
        <DetailBody
          payload={payload}
          subId={subId}
          isPending={isPending}
          latestSub={latestSub}
          entity={entity}
          onRefresh={onRefresh}
        />
      </SheetContent>
    </Sheet>
  );
}

function DetailHeader({ entity, approval }) {
  return (
    <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
      <SheetHeader>
        <div className="flex items-start justify-between">
          <div>
            <SheetTitle className="text-xl font-bold">{entity.display_name || entity.primary_key}</SheetTitle>
            <p className="text-sm text-muted-foreground mt-0.5">
              {entity.primary_key} &middot; {entity.submission_count} submission{entity.submission_count !== 1 ? 's' : ''}
            </p>
          </div>
          <Badge className={'text-sm font-medium border px-3 py-1 ' + statusBadge(approval)} variant="outline">
            {statusLabel(approval)}
          </Badge>
        </div>
      </SheetHeader>
    </div>
  );
}

function DetailActions({ isPending, isAccepted, subId, onApprove, onReject, onClose, onRefresh }) {
  var deletingState = useState(false);
  var deleting = deletingState[0];
  var setDeleting = deletingState[1];

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await zohoAPI.deleteSubmission(subId);
      toast.success('Submission deleted');
      onClose();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
    setDeleting(false);
  }

  if (!subId) return null;

  return (
    <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
      {isPending && (
        <>
          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={function() { onApprove(subId); }} data-testid="detail-approve-btn">
            <Check className="h-3.5 w-3.5" /> Accept
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50" onClick={function() { onReject(subId); }} data-testid="detail-reject-btn">
            <X className="h-3.5 w-3.5" /> Reject
          </Button>
        </>
      )}
      <span className="text-xs text-muted-foreground ml-2 flex items-center gap-1">
        <Pencil className="h-3 w-3" /> Click any field value to edit
      </span>
      {isAccepted && (
        <Button size="sm" variant="outline" className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50 ml-auto" onClick={handleDelete} disabled={deleting} data-testid="detail-delete-btn">
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Delete Submission
        </Button>
      )}
    </div>
  );
}

function DetailBody({ payload, subId, isPending, latestSub, entity, onRefresh }) {
  var editState = useState({});
  var edits = editState[0];
  var setEdits = editState[1];

  var editingState = useState(null);
  var editingField = editingState[0];
  var setEditingField = editingState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var currentPayload = Object.assign({}, payload, edits);
  var sections = groupFieldsIntoSections(currentPayload);
  var hasEdits = Object.keys(edits).length > 0;

  function handleFieldClick(key) {
    setEditingField(key);
  }

  function handleFieldChange(key, value) {
    setEdits(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = value;
      return next;
    });
  }

  function handleFieldBlur() {
    setEditingField(null);
  }

  async function handleSave() {
    if (!hasEdits) return;
    setSaving(true);
    try {
      var updatedPayload = Object.assign({}, payload, edits);
      await zohoAPI.updateSubmissionPayload(subId, updatedPayload);
      toast.success('Submission updated');
      setEdits({});
      setEditingField(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    }
    setSaving(false);
  }

  return (
    <div className="px-6 py-5 space-y-5">
      {hasEdits && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <span className="text-xs text-blue-700 font-medium">{Object.keys(edits).length} field{Object.keys(edits).length > 1 ? 's' : ''} modified</span>
          <Button size="sm" className="h-7 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-edits-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save Changes
          </Button>
        </div>
      )}

      {sections.map(function(sec, idx) {
        return (
          <React.Fragment key={sec.key}>
            {idx > 0 && <Separator />}
            <SectionBlock title={sec.label} sectionKey={sec.key} fields={sec.fields} isPending={isPending} editingField={editingField} edits={edits} onFieldClick={handleFieldClick} onFieldChange={handleFieldChange} onFieldBlur={handleFieldBlur} />
          </React.Fragment>
        );
      })}

      {latestSub && (
        <>
          <Separator />
          <div className="space-y-1" data-testid="detail-section-meta">
            <h4 className="text-sm font-semibold mb-2">Submission Info</h4>
            <div className="bg-slate-50/50 rounded-lg px-4 py-2">
              <StaticFieldRow label="Submission ID" value={latestSub.id} />
              <StaticFieldRow label="Received At" value={latestSub.created_at ? new Date(latestSub.created_at).toLocaleString('en-IN') : '-'} />
              <StaticFieldRow label="Approval Status" value={statusLabel(latestSub.approval_status || 'pending')} />
              {latestSub.approved_by && <StaticFieldRow label="Approved By" value={latestSub.approved_by} />}
              {entity.periods && entity.periods.length > 0 && (
                <StaticFieldRow label="Periods" value={entity.periods.join(', ')} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SectionBlock({ title, sectionKey, fields, isPending, editingField, edits, onFieldClick, onFieldChange, onFieldBlur }) {
  var Icon = SECTION_ICONS[sectionKey] || User;
  return (
    <div className="space-y-1" data-testid={'detail-section-' + sectionKey}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      <div className="bg-slate-50/50 rounded-lg px-4 py-2">
        {fields.map(function(f) {
          var isEditing = editingField === f.key;
          var editedVal = edits[f.key];
          var displayVal = editedVal !== undefined ? editedVal : f.value;

          if (isEditing) {
            return (
              <div key={f.key} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                <span className="text-xs font-medium text-muted-foreground w-2/5 shrink-0">{f.key}</span>
                <Input
                  autoFocus
                  className="h-7 text-sm w-3/5"
                  defaultValue={displayVal != null ? String(displayVal) : ''}
                  onBlur={function(e) { onFieldChange(f.key, e.target.value); onFieldBlur(); }}
                  onKeyDown={function(e) { if (e.key === 'Enter') { onFieldChange(f.key, e.target.value); onFieldBlur(); } }}
                  data-testid={'edit-field-' + f.key}
                />
              </div>
            );
          }

          var hasEdit = editedVal !== undefined;

          return (
            <div
              key={f.key}
              className={'group flex justify-between items-start py-2 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-blue-50/60 rounded -mx-2 px-2 transition-colors duration-100'}
              onClick={function() { onFieldClick(f.key); }}
              data-testid={'field-row-' + f.key}
            >
              <span className="text-xs font-medium text-muted-foreground w-2/5 shrink-0">{f.key}</span>
              <span className={'text-sm text-right break-words max-w-[60%] flex items-center gap-1.5 ' + (hasEdit ? 'text-blue-700 font-medium' : '')}>
                {displayVal != null ? String(displayVal) : '-'}
                <Pencil className="h-3 w-3 text-blue-400 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StaticFieldRow({ label, value }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs font-medium text-muted-foreground w-2/5 shrink-0">{label}</span>
      <span className="text-sm text-right break-words max-w-[60%]">{value != null ? String(value) : '-'}</span>
    </div>
  );
}
